import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarDate, CalendarDateTime, Time } from '@internationalized/date';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

const BRAZIL_TIMEZONE = 'America/Sao_Paulo';

export function datetimeLocalToUTC(datetimeLocal: string): string {
  if (!datetimeLocal) return '';

  const [datePart, timePart] = datetimeLocal.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  const localDate = new Date(year, month - 1, day, hours, minutes, 0);
  const utcDate = fromZonedTime(localDate, BRAZIL_TIMEZONE);
  return utcDate.toISOString();
}

export function utcToDatetimeLocal(isoString: string): string {
  if (!isoString) return '';

  const utcDate = new Date(isoString);
  const brazilDate = toZonedTime(utcDate, BRAZIL_TIMEZONE);

  const year = brazilDate.getFullYear();
  const month = String(brazilDate.getMonth() + 1).padStart(2, '0');
  const day = String(brazilDate.getDate()).padStart(2, '0');
  const hours = String(brazilDate.getHours()).padStart(2, '0');
  const mins = String(brazilDate.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${mins}`;
}

export function formatDateTimeBR(isoString: string): string {
  if (!isoString) return '';
  const utcDate = new Date(isoString);
  const brazilDate = toZonedTime(utcDate, BRAZIL_TIMEZONE);
  return format(brazilDate, 'dd/MM/yyyy HH:mm', { locale: ptBR });
}

export function formatDateBR(isoString: string): string {
  if (!isoString) return '';
  const brazilDate = toZonedTime(new Date(isoString), BRAZIL_TIMEZONE);
  return format(brazilDate, 'dd/MM/yyyy', { locale: ptBR });
}

export function formatTimeBR(isoString: string): string {
  if (!isoString) return '';
  const brazilDate = toZonedTime(new Date(isoString), BRAZIL_TIMEZONE);
  return format(brazilDate, 'HH:mm', { locale: ptBR });
}

export function getCurrentDatetimeLocal(): string {
  const now = new Date();
  const brazilDate = toZonedTime(now, BRAZIL_TIMEZONE);
  return utcToDatetimeLocal(brazilDate.toISOString());
}

export function addMinutesToDatetimeLocal(value: string, minutes: number): string {
  const utc = datetimeLocalToUTC(value);
  const date = new Date(utc);
  date.setMinutes(date.getMinutes() + minutes);
  return utcToDatetimeLocal(date.toISOString());
}

export function addHoursToDatetimeLocal(value: string, hours: number): string {
  return addMinutesToDatetimeLocal(value, hours * 60);
}

export function compareDatetimeLocal(a: string, b: string): number {
  return datetimeLocalToUTC(a).localeCompare(datetimeLocalToUTC(b));
}

export function getEndWhenStartChanges(
  newStart: string,
  currentEnd: string,
  minMinutesAfterStart = 1,
): string {
  if (!newStart) return currentEnd;
  if (!currentEnd) return addMinutesToDatetimeLocal(newStart, minMinutesAfterStart);
  if (compareDatetimeLocal(newStart, currentEnd) > 0) {
    return addMinutesToDatetimeLocal(newStart, minMinutesAfterStart);
  }
  return currentEnd;
}

/** Data local (YYYY-MM-DD) no fuso de Brasília. */
export function getTodayDateOnly(): string {
  const brazilDate = toZonedTime(new Date(), BRAZIL_TIMEZONE);
  return format(brazilDate, 'yyyy-MM-dd');
}

export function addDaysToDateOnly(dateStr: string, days: number): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const js = new Date(year, month - 1, day);
  js.setDate(js.getDate() + days);
  return format(js, 'yyyy-MM-dd');
}

export function dateOnlyToUtcStart(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  return fromZonedTime(localDate, BRAZIL_TIMEZONE).toISOString();
}

export function dateOnlyToUtcEnd(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const localDate = new Date(year, month - 1, day, 23, 59, 59, 999);
  return fromZonedTime(localDate, BRAZIL_TIMEZONE).toISOString();
}

export function formatDateOnlyBR(dateStr: string): string {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  return format(new Date(year, month - 1, day), 'dd/MM/yyyy', { locale: ptBR });
}

export function formatCalendarDateLongBR(date: CalendarDate): string {
  const formatted = format(
    new Date(date.year, date.month - 1, date.day),
    "EEEE, d 'de' MMMM",
    { locale: ptBR },
  );
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

export function dateOnlyToCalendarDate(dateStr: string): CalendarDate {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new CalendarDate(year, month, day);
}

export function calendarDateToDateOnly(date: CalendarDate): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.year}-${pad(date.month)}-${pad(date.day)}`;
}

export function dateOnlyRangeToCalendarRange(
  startDate: string,
  endDate: string,
): { start: CalendarDate; end: CalendarDate } {
  return {
    start: dateOnlyToCalendarDate(startDate),
    end: dateOnlyToCalendarDate(endDate),
  };
}

function calendarDateTimeToLocalString(dateTime: CalendarDateTime): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dateTime.year}-${pad(dateTime.month)}-${pad(dateTime.day)}T${pad(dateTime.hour)}:${pad(dateTime.minute)}`;
}

function localStringToCalendarDateTime(local: string): CalendarDateTime {
  const [datePart, timePart] = local.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  return new CalendarDateTime(year, month, day, hour, minute, 0);
}

export function calendarDateTimeToIsoUtc(dateTime: CalendarDateTime | null): string {
  if (!dateTime) return '';
  return datetimeLocalToUTC(calendarDateTimeToLocalString(dateTime));
}

export function isoUtcToCalendarDateTime(isoString: string): CalendarDateTime | null {
  if (!isoString) return null;
  const local = utcToDatetimeLocal(isoString);
  if (!local) return null;
  return localStringToCalendarDateTime(local);
}

export function getCurrentCalendarDateTime(): CalendarDateTime {
  return localStringToCalendarDateTime(getCurrentDatetimeLocal());
}

export function addMinutesToCalendarDateTime(
  dateTime: CalendarDateTime,
  minutes: number,
): CalendarDateTime {
  const next = addMinutesToDatetimeLocal(calendarDateTimeToLocalString(dateTime), minutes);
  return localStringToCalendarDateTime(next);
}

export function addHoursToCalendarDateTime(
  dateTime: CalendarDateTime,
  hours: number,
): CalendarDateTime {
  return addMinutesToCalendarDateTime(dateTime, hours * 60);
}

export function compareCalendarDateTime(
  dateTime1: CalendarDateTime,
  dateTime2: CalendarDateTime,
): number {
  return compareDatetimeLocal(
    calendarDateTimeToLocalString(dateTime1),
    calendarDateTimeToLocalString(dateTime2),
  );
}

export function getCalendarEndWhenStartChanges(
  newStart: CalendarDateTime | null,
  currentEnd: CalendarDateTime | null,
  minMinutesAfterStart = 1,
): CalendarDateTime | null {
  if (!newStart) return currentEnd;
  if (!currentEnd) return addMinutesToCalendarDateTime(newStart, minMinutesAfterStart);
  if (compareCalendarDateTime(newStart, currentEnd) > 0) {
    return addMinutesToCalendarDateTime(newStart, minMinutesAfterStart);
  }
  return currentEnd;
}

export function calendarDateTimeToDate(dateTime: CalendarDateTime): CalendarDate {
  return new CalendarDate(dateTime.year, dateTime.month, dateTime.day);
}

export function calendarDateTimeToTime(dateTime: CalendarDateTime): Time {
  return new Time(dateTime.hour, dateTime.minute, dateTime.second ?? 0);
}

export function mergeCalendarDateTime(date: CalendarDate, time: Time): CalendarDateTime {
  return new CalendarDateTime(
    date.year,
    date.month,
    date.day,
    time.hour,
    time.minute,
    time.second ?? 0,
  );
}
